import {
  LayoutDashboard,
  Users,
  Zap,
  Wallet,
  WalletCards,
  Receipt,
  ScrollText,
  Bell,
  Building2,
  ChevronsUpDown,
  LogOut,
  UserCircle,
  User,
  KeyRound,
  Package,
  Paintbrush,
  ShoppingBag,
  Plug,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation, useNavigate } from 'react-router'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/shadcn/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { useAuthStore } from '@/store/auth.store'
import { usePermissions } from '@/hooks/usePermissions'
import { PermissionEntityType } from '@/api/model'
import { useGetApiMe, usePostApiLogout } from '@/api/generated/user/user'
import { PasswordChangeDialog } from '@/pages/profile/PasswordChangeDialog'
import { ProfileDialog } from '@/pages/profile/ProfileDialog'

const navGroupsConfig = [
  {
    labelKey: null,
    items: [{ key: 'dashboard', icon: LayoutDashboard, to: '/' }],
  },
  {
    labelKey: 'management',
    items: [
      { key: 'employees', icon: User, to: '/employees' },
      { key: 'groups', icon: Users, to: '/groups' },
      { key: 'notifications', icon: Bell, to: '/notifications' },
      { key: 'permissions', icon: KeyRound, to: '/permissions' },
      { key: 'company', icon: Building2, to: '/company' },
    ],
  },
  {
    labelKey: 'modules',
    items: [
      { key: 'actionModules', icon: Zap, to: '/action-modules' },
      { key: 'serviceModules', icon: Package, to: '/service-modules' },
      { key: 'shopModules', icon: ShoppingBag, to: '/shop-modules' },
    ],
  },
  {
    labelKey: 'finance',
    items: [
      { key: 'wallet', icon: Wallet, to: '/wallet' },
      { key: 'walletTypes', icon: WalletCards, to: '/wallet-types' },
      { key: 'billing', icon: Receipt, to: '/billing' },
    ],
  },
  {
    labelKey: 'other',
    items: [
      { key: 'auditLogs', icon: ScrollText, to: '/audit-logs' },
      { key: 'appDesign', icon: Paintbrush, to: '/app-design' },
      { key: 'serviceConnections', icon: Plug, to: '/service-connections' },
    ],
  },
  {
    labelKey: 'superAdmin',
    items: [
      { key: 'superAdminBilling', icon: Receipt, to: '/super-admin/billing' },
      { key: 'superAdminPermissions', icon: KeyRound, to: '/super-admin/permissions' },
    ],
  },
] as const

// Derived from the config rather than listed by hand, so a new super-admin entry is covered
// without touching the company check below.
const SUPER_ADMIN_ITEM_KEYS = new Set<string>(
  navGroupsConfig.find((group) => group.labelKey === 'superAdmin')?.items.map((item) => item.key) ?? [],
)

/**
 * Per-item sidebar visibility. Mirrors the route guards in `main.tsx`. The
 * super-admin items are role-gated (`role === "Administrator"`); everything else
 * is permission-gated. Groups with no visible items are dropped entirely.
 */
function useIsNavItemVisible() {
  const { userHasPermission, userHasAnyPermission } = usePermissions()
  const role = useAuthStore((s) => s.role)
  const companyId = useAuthStore((s) => s.companyId)
  const P = PermissionEntityType

  // An administrator keeps his portal permissions even with no company chosen, so the entries
  // would still render — but every `/api/companies/current/...` route answers 400 without a
  // CompanyId claim, so each one leads to an empty or broken page. Only a company management
  // user is unaffected: he always has exactly one company.
  const isAdministratorWithoutCompany = role === 'Administrator' && companyId === null

  return (key: string): boolean => {
    if (isAdministratorWithoutCompany && !SUPER_ADMIN_ITEM_KEYS.has(key)) {
      return false
    }

    switch (key) {
      case 'dashboard':
        return userHasPermission(P.General, 'Dashboard')
      case 'employees':
        return userHasPermission(P.General, 'AccountManagement')
      case 'groups':
        return userHasAnyPermission(P.Groups, 'View')
      case 'notifications':
        return userHasPermission(P.General, 'GenericInAppNotifications')
      case 'permissions':
        return userHasPermission(P.Permissions, 'View')
      case 'company':
        return (
          userHasPermission(P.General, 'CompanyManagement') ||
          userHasAnyPermission(P.SubCompanies, 'View') ||
          userHasAnyPermission(P.SubCompanies, 'Edit') ||
          userHasAnyPermission(P.SubCompanies, 'Create') ||
          userHasAnyPermission(P.SubCompanies, 'Delete')
        )
      case 'actionModules':
        return userHasAnyPermission(P.ActionModules, 'View')
      case 'serviceModules':
        return userHasAnyPermission(P.ServiceModules, 'View')
      case 'shopModules':
        return userHasAnyPermission(P.ShopModules, 'View')
      case 'incentivation':
        return userHasAnyPermission(P.ActionModules, 'Incentivate')
      case 'wallet':
        return userHasPermission(P.General, 'CompanyWallet')
      case 'walletTypes':
        return userHasPermission(P.General, 'CompanyWallet')
      case 'billing':
        return userHasPermission(P.General, 'Billing')
      case 'auditLogs':
        return userHasPermission(P.General, 'AuditLog')
      case 'appDesign':
        return userHasPermission(P.General, 'Customization')
      case 'serviceConnections':
        return userHasPermission(P.ServiceConnections, 'View')
      case 'superAdminBilling':
        return role === 'Administrator' && userHasPermission(P.AdministratorGeneral, 'Billing')
      case 'superAdminPermissions':
        return role === 'Administrator' && userHasPermission(P.AdministratorPermissions, 'View')
      default:
        return true
    }
  }
}

function getInitials(firstName?: string | null, lastName?: string | null) {
  return [firstName, lastName]
    .filter(Boolean)
    .map((n) => n![0].toUpperCase())
    .join('')
}

export function AppSidebar() {
  const { t } = useTranslation('sidebar')
  const { open } = useSidebar()
  const location = useLocation()
  const navigate = useNavigate()
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const { mutateAsync: logout } = usePostApiLogout()
  const { data: meResponse } = useGetApiMe()
  const me = meResponse?.data
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false)
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false)

  const isNavItemVisible = useIsNavItemVisible()

  const navGroups = navGroupsConfig
    .map((group) => ({
      label: group.labelKey ? t(`groups.${group.labelKey}`) : null,
      items: group.items
        .filter((item) => isNavItemVisible(item.key))
        .map((item) => ({ ...item, label: t(`items.${item.key}`) })),
    }))
    // Drop groups whose items are all hidden by permission/role.
    .filter((group) => group.items.length > 0)

  const fullName = [me?.firstName, me?.lastName].filter(Boolean).join(' ') || t('footer.userFallback')
  const initials = getInitials(me?.firstName, me?.lastName) || '?'

  const handleLogout = async () => {
    // Log out on the server first (revoke refresh token / delete cookie),
    // then clear local state — like the LogoutButton in the CustomerPortal.
    try {
      await logout({})
    } catch {
      // Logout request failed: clear local state anyway.
    } finally {
      clearAuth()
      navigate('/login', { replace: true })
    }
  }

  return (
    <>
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-25 p-6">
        {open && (
          <img
            src="/logos/Bonisoft_logo_iceblue_text_white.svg"
            alt="Bonidriver"
            className="h-auto w-auto"
          />
       )}
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group, i) => (
          <SidebarGroup key={i}>
            {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
            <SidebarMenu>
              {group.items.map((item) => {
                const isActive =
                  item.to === '/'
                    ? location.pathname === '/'
                    : location.pathname === item.to ||
                      location.pathname.startsWith(item.to + '/')
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                      <NavLink to={item.to}>
                        <item.icon />
                        <span>{item.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="rounded-full text-white bg-primary">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{fullName}</span>
                    <span className="truncate text-xs text-muted-foreground">{me?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="right"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuItem onClick={() => setIsProfileDialogOpen(true)}>
                  <UserCircle className="mr-2 size-4" />
                  {t('footer.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsPasswordDialogOpen(true)}>
                  <KeyRound className="mr-2 size-4" />
                  {t('footer.changePassword')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 size-4" />
                  {t('footer.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>

    <PasswordChangeDialog isOpen={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen} />
    <ProfileDialog isOpen={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen} />
    </>
  )
}
